import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import AdminPageShell from '../AdminPageShell';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { fetchAdminFormateurActivityDetail } from '../../../services/academiaAdminService';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { AcademicPanelCard } from './AcademicPanelCard';

type ActivityDetail = {
  id?: string;
  when?: string;
  categorie?: string;
  categorieLabel?: string;
  action?: string;
  formateur?: {
    id?: string;
    label?: string;
    specialite?: string | null;
    estActif?: boolean;
    telephone?: string | null;
    email?: string | null;
  };
  idSession?: string | null;
  idReference?: string | null;
  detailsComplets?: string | null;
  detailsStructure?: unknown;
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return '—';
  }
}

function DetailGrid({ rows }: { rows: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={row.label}>
          <dt className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{row.label}</dt>
          <dd className="mt-1 text-sm font-medium text-slate-900">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function renderStructuredDetails(structured: unknown) {
  if (!structured || typeof structured !== 'object') return null;
  const obj = structured as Record<string, unknown>;
  const contexte =
    obj.contexte && typeof obj.contexte === 'object' ? (obj.contexte as Record<string, unknown>) : null;
  const avant = obj.avant && typeof obj.avant === 'object' ? (obj.avant as Record<string, unknown>) : null;
  const apres = obj.apres && typeof obj.apres === 'object' ? (obj.apres as Record<string, unknown>) : null;
  const commentaire = typeof obj.commentaire === 'string' ? obj.commentaire : null;

  if (!contexte && !commentaire && !avant && !apres) return null;

  return (
    <div className="space-y-4">
      {commentaire ? (
        <p className="rounded-lg border border-outline-variant/20 bg-surface-container-low px-3 py-2 text-sm text-slate-800">
          {commentaire}
        </p>
      ) : null}
      {contexte ? (
        <DetailGrid
          rows={Object.entries(contexte).map(([key, value]) => ({
            label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
            value:
              value == null
                ? '—'
                : typeof value === 'object'
                  ? JSON.stringify(value)
                  : String(value),
          }))}
        />
      ) : null}
      {avant || apres ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {avant ? (
            <div className="rounded-lg border border-outline-variant/20 bg-surface-container-low px-3 py-2">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Avant</p>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-800">{JSON.stringify(avant, null, 2)}</pre>
            </div>
          ) : null}
          {apres ? (
            <div className="rounded-lg border border-outline-variant/20 bg-surface-container-low px-3 py-2">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Après</p>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-800">{JSON.stringify(apres, null, 2)}</pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function FormateurActivityDetailPanel() {
  const { activityId } = useParams<{ activityId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ActivityDetail | null>(null);

  const load = useCallback(async () => {
    if (!activityId) return;
    setLoading(true);
    setError(null);
    try {
      const row = await fetchAdminFormateurActivityDetail(activityId);
      setData(row);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Activité introuvable');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activityId]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(load);

  const title = data?.action ?? 'Détail de l\'activité';

  return (
    <AdminPageShell
      title={title}
      description="Journal des activités formateurs"
      sideCard={{
        label: 'Formateur',
        value: data?.formateur?.label ?? '—',
        helper: data?.categorieLabel ?? 'Détail',
        sideLinks: [
          { label: 'Retour au journal', to: '/admin/activites-formateurs', icon: 'history' },
          { label: 'Gestion formateurs', to: '/admin/formateurs', icon: 'cast_for_education' },
        ],
      }}
      isLoading={loading && !data && !error}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement…"
    >
      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
      ) : null}

      {!error && data ? (
        <div className="space-y-4">
          <AcademicPanelCard
            title="Résumé"
            icon={<LucideSymbolIcon name="cast_for_education" tone="primary" size={20} />}
          >
            <DetailGrid
              rows={[
                { label: 'Date', value: formatDate(data.when) },
                { label: 'Catégorie', value: data.categorieLabel ?? data.categorie ?? '—' },
                { label: 'Activité', value: data.action ?? '—' },
                { label: 'Formateur', value: data.formateur?.label ?? '—' },
                { label: 'Spécialité', value: data.formateur?.specialite ?? '—' },
                {
                  label: 'Statut',
                  value: data.formateur?.estActif ? 'Actif' : 'Inactif',
                },
                { label: 'Téléphone', value: data.formateur?.telephone ?? '—' },
                { label: 'E-mail', value: data.formateur?.email ?? '—' },
                { label: 'Session liée', value: data.idSession ?? '—' },
                { label: 'Référence', value: data.idReference ?? '—' },
              ]}
            />
          </AcademicPanelCard>

          <AcademicPanelCard
            title="Détails enregistrés"
            icon={<LucideSymbolIcon name="description" tone="primary" size={20} />}
          >
            {renderStructuredDetails(data.detailsStructure) ?? (
              <pre className="max-h-96 overflow-auto rounded-lg bg-surface-container-high p-3 text-xs text-slate-800 whitespace-pre-wrap">
                {data.detailsComplets ?? 'Aucun détail supplémentaire.'}
              </pre>
            )}
          </AcademicPanelCard>

          <p className="text-xs text-on-surface-variant">
            Les opérations admin (validation document, paiement prestation) sont consultables dans{' '}
            <Link to="/admin/security" className="font-semibold text-primary hover:underline">
              Sécurité & traçabilité
            </Link>
            .
          </p>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
